import type { ILabel } from 'src/pages/setting/labels.types';
import type { TableHeadCellProps } from 'src/components/table';
import type { ITenant } from 'src/pages/tenants/tenants.types';
import type { ITicket } from 'src/pages/tickets/tickets.types';
import type { IAccount } from 'src/pages/accounts/accounts.types';
import type { ITeamItem, IUserItem } from 'src/pages/setting/setting.types';
import type { IContact, IDiscount } from 'src/pages/contacts/contacts.types';
import type {
  Filters,
  TeamList,
  TicketsList,
  AccountList,
  ChannelData,
  TemplateList,
  BroadcastData,
  IConversation,
  CustomLabelsList,
  IUserTableFilters,
} from 'src/types/user';

import { varAlpha } from 'minimal-shared/utils';
import { useBoolean, useSetState } from 'minimal-shared/hooks';
import { useMemo, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Tooltip from '@mui/material/Tooltip';
import TableBody from '@mui/material/TableBody';
import { useTheme } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import { Tab, Tabs, Button, Skeleton, TableRow, TableCell, Typography } from '@mui/material';

import { useTranslate } from 'src/locales';
import { useCampaignContext } from 'src/pages/campaign/hooks';
import { useCallContext } from 'src/pages/chat/hooks/use-call-context';
import { useModulePermissions } from 'src/pages/auth/hooks/useModulePermissions';

import { toast } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';
import {
  useTable,
  emptyRows,
  rowInPage,
  TableNoData,
  getComparator,
  TableEmptyRows,
  TableRowCustom,
  TableHeadCustom,
  TableSelectedAction,
  TablePaginationCustom,
} from 'src/components/table';

import { Label } from '../label';
import { TableToolbar } from './table-toolbar';
import { ConfirmDialog } from '../custom-dialog';

type TableCustomProps = {
  columns: TableHeadCellProps[];
  data:
    | TicketsList[]
    | IUserItem[]
    | IAccount[]
    | IContact[]
    | AccountList[]
    | TeamList[]
    | ITeamItem[]
    | CustomLabelsList[]
    | ChannelData[]
    | BroadcastData[]
    | BroadcastData[]
    | ILabel[]
    | TemplateList[]
    | ITenant[]
    | IConversation[]
    | IDiscount[]
    | ITicket[];
  tableToolbar?: boolean;
  actionButton?: string;
  actionButtonHandler?: () => void;
  editHref: (id: string) => string;
  contactDetailHref?: (id: string) => string;
  accountDetailHref?: (id: string) => string;
  userDetailHref?: (id: string) => string;
  teamsDetailHref?: (id: string) => string;
  handleEditContact?: (id: string) => string | Promise<void>;
  tenantUserDetailsHref?: (tenantId: string, userId: string) => string;
  tenantInvoiceDetailsHref?: (tenantId: string, userId: string) => string;
  isToolbarVisible?: boolean;
  isCheckboxVisible?: boolean;
  rowHeight?: boolean;
  isSelectRowPremission?: boolean;
  isActionButton?: Array<'import' | 'export'>;
  actionhref?: string;
  queryParams?: Filters[];
  onQueryChange?: (newParams: any) => void;
  totalRecordLength?: number;
  sortFldList?: string[];
  isShowCreateBtn?: boolean;
  isSelectRecordDialog?: boolean;
  setRecordInputHandler?: (row: any) => void;
  isSearchFullWidth?: boolean;
  handleDeleteRecords?: (ids: string[]) => void;
  handleIVRConnection?: (id: string) => void;
  handleCustomiseDID?: (id: string) => void;
  moduleName?: string;
  loader?: boolean;
  handleResendMail?: (id: string) => string;
  handlePreview?: (id: string) => string;
  playCallRecording?: (id: string) => void;
  isCampaignTamplate?: boolean;
  getSelectedRows?: (id: any, selectAllFilter?: any) => void;
  isSelectAudience?: boolean;
  isCampaining?: boolean;
  onFileSelect?: (fileData: any) => void;
  onExportContact?: () => void;
  currentModule?: string;
  currentlyPlayingId?: string | null;
  advanceFilterFields?: any;
  isAdvanceFilter?: boolean;
  refreshPage?: () => void;
  refreshing?: boolean;
  tabFilters?: boolean;
  customTabs?: any;
  handleLoginAsUser?: (row: any) => void;
  handleOpenConversation?: (row: any) => void;
  isDisableSync?: boolean;
  userRole?: string;
  isSupportReport?: boolean;
  isSearchInput?: boolean;
  isPrivacyPolicyUrls?: {
    message: string;
    links: { label: string; url: string }[];
  } | null;
  displayPagination?: boolean;
  isTenantInvoice?: boolean;
  isSuperAdmin?: boolean;
  handleMarkAsRead?: (id: string) => void;
  currentTenantId?: string;
  handlePayNowButton?: (id: any) => void;
};

export function TableCustom({
  columns,
  data,
  tableToolbar,
  actionButton = '',
  actionButtonHandler = () => {},
  editHref,
  isToolbarVisible = true, // Set visibility of search box and the three-dot options menu
  isSelectRowPremission = true,
  isActionButton = [],
  actionhref = '',
  handleEditContact,
  rowHeight,
  queryParams = [],
  onQueryChange = () => {},
  totalRecordLength = 0,
  sortFldList,
  isShowCreateBtn = true,
  isSelectRecordDialog = false,
  setRecordInputHandler,
  isSearchFullWidth = false,
  handleDeleteRecords,
  handleCustomiseDID,
  handleIVRConnection,
  moduleName = '',
  loader = false,
  handleResendMail,
  handlePreview,
  isCampaignTamplate = false,
  getSelectedRows = () => {},
  isSelectAudience = false,
  isCampaining = false,
  onFileSelect = () => {},
  onExportContact = () => {},
  currentModule = '',
  playCallRecording = () => {},
  currentlyPlayingId = null,
  advanceFilterFields = [],
  isAdvanceFilter = false,
  refreshPage = undefined,
  refreshing = false,
  contactDetailHref,
  accountDetailHref,
  userDetailHref,
  teamsDetailHref,
  tabFilters = false,
  customTabs = [],
  isPrivacyPolicyUrls = null,
  handleLoginAsUser = () => {},
  handleOpenConversation = () => {},
  userRole = '',
  isDisableSync = false,
  isSupportReport = false,
  displayPagination = true,
  isSearchInput = true,
  isTenantInvoice = false,
  handleMarkAsRead = () => {},
  tenantUserDetailsHref,
  tenantInvoiceDetailsHref,
  isSuperAdmin = false,
  currentTenantId = '',
  handlePayNowButton = () => {},
}: TableCustomProps) {
  // ======================
  const [selectDefault, setSelectDefault] = useState(true);
  const theme = useTheme();
  const { makeCall, registered, handleCallUserDetails } = useCallContext();

  //======================
  const { state: campaignState, setField } = useCampaignContext();
  const table = useTable({
    defaultDense: rowHeight ? false : !tabFilters,
    defaultRowsPerPage: 10,
    defaultselectAll: campaignState.selectAllFilter,
  });

  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const { t } = useTranslate('translation');

  const myTableHeadElement = document.getElementsByClassName('myTableHead');

  const [tableData, setTableData] = useState<TableCustomProps['data']>(data);

  const confirmDialog = useBoolean();

  const userFilters = useSetState<IUserTableFilters>({ name: '', role: [], status: 'all' });
  const { state: currentFilters, setState: updateFilters } = userFilters;

  const deleteRecordWarning = t('DELETE_RECORD_WARNING').replace(
    '{MODULE}',
    t(moduleName ?? 'UNKNOWN')
  );

  const { isCreate, isView, isImport, isExport, isEdit, isDelete } =
    useModulePermissions(moduleName);

  useEffect(() => {
    setTableData(data);
  }, [data]);

  const dataFiltered = useMemo(
    () =>
      applyFilter({
        inputData: tableData,
        comparator: getComparator(table.order, table.orderBy),
        filters: currentFilters,
      }),
    [tableData, table.order, table.orderBy, currentFilters]
  );

  const dataInPage = rowInPage(
    dataFiltered.filter((item): item is TicketsList => 'subject' in item),
    table.page,
    table.rowsPerPage
  );

  const canReset =
    !!currentFilters.name || currentFilters.role.length > 0 || currentFilters.status !== 'all';

  const notFound = (!dataFiltered.length && canReset) || !dataFiltered.length;

  const handleDeleteRow = useCallback(
    (id: string) => {
      const deleteRow = tableData.filter((row) => row.id !== id);

      toast.success(t('ROW_DELETE_SUCCESS'));

      if (tableData.every((row) => 'subject' in row)) {
        setTableData(deleteRow as TicketsList[]);
      } else {
        setTableData(deleteRow as IUserItem[]);
      }

      table.onUpdatePageDeleteRow(dataInPage.length);
    },
    [dataInPage.length, t, table, tableData]
  );

  // ===============================================================
  const [filters, setFilters] = useState<{ [key: string]: string }>({});
  const [dateTimeFiltre, setDateTimeFilter] = useState<any>([]);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

  const handleFilterChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    column: string
  ) => {
    table.setPage(0);
    let value = event.target.value;

    // Special handling for phone numbers starting with '0'
    if (column === 'phone' && value.startsWith('0')) {
      value = '966' + value.slice(1);
    }

    setFilters((prevFilters) => {
      const newFilters = { ...prevFilters };

      if (value === '') {
        // ✅ Remove the filter key if value is empty
        delete newFilters[column];
      } else {
        newFilters[column] = value;
      }

      return newFilters;
    });

    // If value is empty, reset the full data
    if (value === '') {
      setTableData(data);
    } else {
      // Optional: implement filtering logic based on newFilters
      // const filteredData = data.filter(...);
      // setTableData(filteredData);
    }
  };

  const handleDeleteRecord = (id: string) => {
    confirmDialog.onTrue();
    setSelectedRowId(id);
  };

  // ===============================================================
  const [resetFilterKey, setResetFilterKey] = useState(0);
  const handleRemoveAllFilter = () => {
    // Reset all filters
    setFilters({});
    setDateTimeFilter([]);
    updateFilters({ name: '', role: [], status: 'all' });
    table.setAdvanceFilter([]);
    table.setPage(0); // reset pagination

    // Optionally reset the data shown (if needed immediately)
    setTableData(data);

    // Trigger query update if needed
    const updatedFilterData = {
      orderBy: table.orderBy ?? '',
      page: 0,
      rowsPerPage: table.rowsPerPage ?? 5,
      globalSearch: '',
      fieldSearch: [],
      order: table.order ?? 'asc',
      TabFilter: 'all',
      dateTimeFiltre: [],
      advanceFilter: [],
    };

    onQueryChange(updatedFilterData);
    setResetFilterKey((prev) => prev + 1);
  };

  // ===============================================================

  useEffect(() => setTableData(data), [data]);

  useEffect(() => {
    if (isFirstLoad) {
      setSelectDefault(false);
      setIsFirstLoad(false); // Mark initial load as complete
      return; // Skip the first execution
    }

    setSelectDefault(false);
  }, [table.selectAllFilter]);

  useEffect(() => {
    if (isFirstLoad) {
      setIsFirstLoad(false); // Mark initial load as complete
      return; // Skip the first execution
    }

    const updatedFilterData = {
      orderBy: table.orderBy ?? '',
      page: table.page,
      rowsPerPage: table.rowsPerPage ?? 5,
      globalSearch: currentFilters.name ?? '',
      fieldSearch: filters ?? [],
      order: table.order ?? 'asc',
      TabFilter: currentFilters.status ?? 'all',
      dateTimeFiltre,
      advanceFilter: table.advanceFilter ?? [],
    };
    onQueryChange(updatedFilterData);
  }, [
    currentFilters.name,
    currentFilters.status,
    table.orderBy,
    table.order,
    table.page,
    table.rowsPerPage,
    filters,
    dateTimeFiltre,
    table.advanceFilter,
  ]);

  useEffect(() => {
    if (isCampaining) return;
    if (!isCampaignTamplate || !isSelectAudience) {
      table.setSelected([]);
    }
  }, [table.page]);

  useEffect(() => {
    if (
      campaignState?.selectedTemplate &&
      campaignState.selectedTemplate.id &&
      isCampaignTamplate
    ) {
      table.setSelected([campaignState.selectedTemplate.id]);
      return;
    }
  }, [campaignState]);

  useEffect(() => {
    if (!getSelectedRows) return;
    getSelectedRows(table.selected, table.selectAllFilter);
  }, [table.selected]);

  const renderConfirmDialog = () => (
    <ConfirmDialog
      open={confirmDialog.value}
      onClose={() => {
        confirmDialog.onFalse();
        setSelectedRowId(null);
      }}
      title={t('DELETE')}
      content={
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('DELETE_RECORD_DESC')} <strong> {selectedRowId ? 1 : table.selected.length} </strong>
            {`${t(moduleName ?? 'UNKNOWN')}  `}
            {selectedRowId || table.selected.length === 1 ? t('RECORD') : t('RECORDS')} ?
          </Typography>
          {moduleName && ['CONTACT', 'CHANNEL'].includes(moduleName) && (
            <Typography variant="body2" color="text.secondary">
              {deleteRecordWarning}
            </Typography>
          )}
        </>
      }
      action={
        <Button
          variant="contained"
          color="error"
          onClick={() => {
            const deleteRecords = selectedRowId ? [selectedRowId] : table.selected;
            handleDeleteRecords?.(deleteRecords);
            confirmDialog.onFalse();
            setSelectedRowId(null);
            table.setSelected([]);
          }}
        >
          {t('DELETE')}
        </Button>
      }
    />
  );

  const handleSelectAll = () => {
    table.setSelectAllFilter((prev: any) => {
      const newActive = !prev.active;

      return {
        active: newActive,
        filterType: newActive ? 'all' : 'in',
        ids: [],
      };
    });

    // Clear selected rows immediately when turning OFF "select all"
    if (table.selectAllFilter.active) {
      table.setSelected([]);
    }
  };

  useEffect(() => {
    if (!isSelectAudience) return;

    const { active, filterType, ids } = table.selectAllFilter;
    const allIds = dataFiltered.map((row) => row.id);
    const firstRenderForedit = selectDefault && campaignState.isEdit;

    if (active) {
      if (filterType === 'all') {
        // All selected
        table.onSelectAllRows(true, firstRenderForedit ? campaignState?.selectedContacts : allIds);
        table.setSelected(firstRenderForedit ? campaignState?.selectedContacts : allIds);
      } else if (filterType === 'not in') {
        // All selected except some
        const filteredIds = allIds.filter((id) => !ids.includes(id));
        table.onSelectAllRows(true, filteredIds);
        table.setSelected(filteredIds);
      }
    } else {
      if (filterType === 'in') {
        // Only specific IDs selected
        const selectedIds = ids.filter((id: any) => allIds.includes(id)); // safety check
        table.onSelectAllRows(false, selectedIds);
        table.setSelected(selectedIds);
      } else {
        // Optional: fallback if no filterType match
        table.setSelected([]);
      }
    }
  }, [table.selectAllFilter, dataFiltered, isSelectAudience]);

  useEffect(() => {
    setField('selectAllFilter', table.selectAllFilter);
  }, [table.selectAllFilter]);

  const resetPagination = () => {
    table.setPage(0);
  };

  // Calculate updated columns before rendering
  const newColumnCount = columns.length;
  const adjustFactor = newColumnCount < 10 ? newColumnCount : newColumnCount - 10 + 1;

  const updateColumns =
    (!isEdit && !isDelete) || moduleName === 'TENANTS'
      ? columns
          .map((item: any) => ({
            ...item,
            width: item?.width + adjustFactor * 100,
          }))
          ?.filter((item) => item?.id !== 'action')
      : columns;

  const handleFilterStatus = useCallback(
    (event: React.SyntheticEvent, newValue: string) => {
      table.onResetPage();
      updateFilters({ status: newValue });
    },
    [updateFilters, table]
  );

  // ============================================================================
  const makeCallDialog = useBoolean(false);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState<any | null>(null);

  const handlePhoneNumberClick = (value: any) => {
    setSelectedPhoneNumber(value);
    makeCallDialog.onTrue();
  };

  const renderMakeCallDialog = () => (
    <ConfirmDialog
      open={makeCallDialog.value}
      onClose={() => {
        makeCallDialog.onFalse();
        setSelectedPhoneNumber(null);
      }}
      title={t('MAKE_CALL')}
      content={
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('DO_YOU_WANT_TO_CALL')} <strong>{selectedPhoneNumber?.phone}</strong>?
          </Typography>
          {!registered && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {t('PBX_NOT_REGISTERED_ERROR')}
            </Typography>
          )}
        </>
      }
      action={
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            if (selectedPhoneNumber) {
              makeCall(selectedPhoneNumber?.phone?.replace('+', ''));
              handleCallUserDetails(selectedPhoneNumber);
            }
            makeCallDialog.onFalse();
            setSelectedPhoneNumber(null);
          }}
          disabled={!registered}
        >
          {t('CALL')}
        </Button>
      }
    />
  );

  // ============================================================================
  return (
    <>
      <Card sx={{ m: 1 }}>
        {tabFilters && (
          <Tabs
            value={currentFilters.status}
            onChange={handleFilterStatus}
            sx={{
              px: 2.5,
              boxShadow: `inset 0 -2px 0 0 ${varAlpha(theme.vars.palette.grey['500Channel'], 0.08)}`,
            }}
          >
            {customTabs.map((tab: any) => (
              <Tab
                key={tab.value}
                value={tab.value}
                label={t(tab.label)}
                iconPosition="end"
                icon={
                  <Label
                    variant={
                      ((tab.value === 'all' || tab.value === currentFilters.status) && 'filled') ||
                      'soft'
                    }
                    color={tab.color}
                  >
                    {tab.count}
                  </Label>
                }
              />
            ))}
          </Tabs>
        )}

        {tableToolbar && (
          <TableToolbar
            filters={userFilters}
            onResetPage={table.onResetPage}
            options={{ roles: [] }}
            actionButtonLabel={actionButton}
            actionButtonHandler={actionButtonHandler}
            isToolbarVisible={isToolbarVisible}
            isActionButton={isActionButton}
            actionhref={actionhref}
            isShowCreateBtn={isShowCreateBtn}
            isSearchFullWidth={isSearchFullWidth}
            onFileSelect={onFileSelect}
            onExportContact={onExportContact}
            currentModule={currentModule}
            isCreate={['call', 'template']?.includes(moduleName) ? true : isCreate}
            isImport={isImport}
            isExport={['conversation']?.includes(moduleName) ? true : isExport}
            advanceFilterFields={advanceFilterFields}
            setAdvanceFilter={table.setAdvanceFilter}
            advanceFilters={table.advanceFilter}
            isAdvanceFilter={isAdvanceFilter}
            refreshPage={refreshPage}
            refreshing={refreshing}
            resetPagination={resetPagination}
            isPrivacyPolicyUrls={isPrivacyPolicyUrls}
            isDisableSync={isDisableSync}
            selectedfilters={filters}
            dateTimeFiltre={dateTimeFiltre}
            handleRemoveAllFilter={handleRemoveAllFilter}
            isSearchInput={isSearchInput}
          />
        )}
        <Box sx={{ position: 'relative' }}>
          {!isSelectRecordDialog && !isCampaignTamplate && !isSelectAudience && (
            <TableSelectedAction
              dense={table.dense}
              numSelected={table.selected.length}
              rowCount={dataFiltered.length}
              onSelectAllRows={(checked) => {
                table.onSelectAllRows(
                  checked,
                  dataFiltered.map((row) => row.id)
                );
              }}
              sx={{ height: `${myTableHeadElement[0]?.scrollHeight || 0}px` }}
              action={
                isDelete && (
                  <Tooltip title={t('DELETE')}>
                    <IconButton color="primary" onClick={confirmDialog.onTrue}>
                      <Iconify
                        icon="solar:trash-bin-trash-bold"
                        sx={{ color: 'var(--palette-error-main) !important' }}
                      />
                    </IconButton>
                  </Tooltip>
                )
              }
            />
          )}

          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ width: '100%' }}>
              <TableHeadCustom
                order={table.order}
                orderBy={table.orderBy}
                headCells={updateColumns}
                rowCount={dataFiltered.length}
                numSelected={table.selected.length}
                onSort={table.onSort}
                onSelectAllRows={(checked) =>
                  table.onSelectAllRows(
                    checked,
                    dataFiltered.map((row) => row.id)
                  )
                }
                handleFilterChange={handleFilterChange}
                selectRowPremission={isSelectRowPremission}
                sortFldList={sortFldList}
                isCampaignTamplate={isCampaignTamplate}
                isEdit={isEdit}
                isDelete={isDelete}
                setDateTimeFilter={setDateTimeFilter}
                isCampaining={isCampaining}
                handleToggleSelectAll={handleSelectAll}
                selectAllFilter={table.selectAllFilter}
                setPage={table.setPage}
                count={totalRecordLength}
                defaultBedgecondition={selectDefault && campaignState.isEdit}
                selectedContact={campaignState?.selectedContacts}
                dateTimeFiltre={dateTimeFiltre}
                moduleName={moduleName}
                resetFilterKey={resetFilterKey}
              />
              <TableBody>
                {loader ? (
                  Array.from({ length: moduleName === 'billing' ? 5 : 10 }).map((_, index) => (
                    <TableRow key={index}>
                      {columns.map((col, colIndex) => (
                        <TableCell key={colIndex}>
                          <Skeleton variant="text" width="100%" height={35} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <>
                    <TableRowCustom
                      t={t}
                      rows={dataFiltered}
                      table={table}
                      editHref={editHref}
                      contactDetailHref={contactDetailHref}
                      accountDetailHref={accountDetailHref}
                      teamsDetailHref={teamsDetailHref}
                      userDetailHref={userDetailHref}
                      onDeleteRow={handleDeleteRow}
                      headCells={updateColumns}
                      selectRowPremission={isSelectRowPremission}
                      handleEditContact={handleEditContact}
                      handleDeleteRecord={handleDeleteRecord}
                      handleCustomiseDID={handleCustomiseDID}
                      handleIVRConnection={handleIVRConnection}
                      isSelectRecordDialog={isSelectRecordDialog}
                      setRecordInputHandler={setRecordInputHandler}
                      handleResendMail={handleResendMail}
                      handlePreview={handlePreview}
                      isCampaignTamplate={isCampaignTamplate}
                      isEdit={isEdit}
                      isDelete={isDelete}
                      playCallRecording={playCallRecording}
                      currentlyPlayingId={currentlyPlayingId}
                      selectAllFilter={table.selectAllFilter}
                      moduleName={moduleName}
                      handleLoginAsUser={handleLoginAsUser}
                      userRole={userRole}
                      isSupportReport={isSupportReport}
                      handlePhoneNumberClick={handlePhoneNumberClick}
                      handleOpenConversation={handleOpenConversation}
                      isTenantInvoice={isTenantInvoice}
                      handleMarkAsRead={handleMarkAsRead}
                      tenantUserDetailsHref={tenantUserDetailsHref}
                      tenantInvoiceDetailsHref={tenantInvoiceDetailsHref}
                      isSuperAdmin={isSuperAdmin}
                      currentTenantId={currentTenantId}
                      handlePayNowButton={handlePayNowButton}
                    />

                    <TableEmptyRows
                      height={table.dense ? 56 : 56 + 20}
                      emptyRows={emptyRows(table.page, table.rowsPerPage, dataFiltered.length)}
                    />

                    <TableNoData notFound={notFound} />
                  </>
                )}
              </TableBody>
            </Table>
          </Scrollbar>
        </Box>
        {displayPagination && (
          <TablePaginationCustom
            page={table.page}
            dense={table.dense}
            count={totalRecordLength}
            rowsPerPage={table.rowsPerPage}
            onPageChange={table.onChangePage}
            onChangeDense={table.onChangeDense}
            onRowsPerPageChange={table.onChangeRowsPerPage}
          />
        )}
      </Card>
      {renderConfirmDialog()}
      {renderMakeCallDialog()}
    </>
  );
}

// ----------------------------------------------------------------------

type ApplyFilterProps = {
  inputData:
    | TicketsList[]
    | IUserItem[]
    | AccountList[]
    | TeamList[]
    | ITeamItem[]
    | IAccount[]
    | IContact[]
    | CustomLabelsList[]
    | ChannelData[]
    | BroadcastData[]
    | BroadcastData[]
    | ILabel[]
    | TemplateList[]
    | ITenant[]
    | IConversation[]
    | IDiscount[]
    | ITicket[];
  filters: IUserTableFilters;
  comparator: (a: any, b: any) => number;
};

function applyFilter({ inputData, comparator, filters }: ApplyFilterProps) {
  const { status } = filters;

  const stabilizedThis = inputData.map((el, index) => [el, index] as const);

  // stabilizedThis.sort((a, b) => {
  //   const order = comparator(a[0], b[0]);
  //   if (order !== 0) return order;
  //   return a[1] - b[1];
  // });

  inputData = stabilizedThis.map((el) => el[0]) as
    | TicketsList[]
    | IUserItem[]
    | AccountList[]
    | IAccount[]
    | IContact[];

  if (status !== 'all') {
    if (inputData.length && 'status' in inputData[0]) {
      inputData = (inputData as IUserItem[]).filter((user) => user.status === status);
    }
  }

  return inputData;
}
